import {
  IHouseStepAboutHousehold,
  IWizardHouseState,
  TStepErrors,
  TStepPayload,
  TWizardStepComponentEditable,
  TWizardStepComponentEditableView,
  WIZARD_HOUSE_STEPS_PATHS
} from "../../../interfaces";
import { Form, Input, Select } from "antd";
import { StepViewOnlyValue } from "../../../Student/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createCheckIsReadonlyStepField,
  getChangedFields,
  getStepErrorsFromResponse
} from "../../../utils";

import { HouseAPI } from "../../../../../API/houseApi";
import { getLanguage } from "../../../../../utils/fetchMapInfo";
import { TLang } from "../../../../../assets/data/isoNameLanguage";
import {
  HouseCharacteristicsValues,
  HousePetsValues,
  ISelectItem,
  THousePets
} from "../../../../../interfaces/interfaces";
import styled from "styled-components/macro";
import { EditableListOfItems } from "../../../../EditableListOfItems";
import { useContextHospi } from "../../../../../context/ContextHospi";
import {
  RadioGroup as RadioGroupOriginal,
  WizardButtonSelect
} from "../../../components";

const statePath = WIZARD_HOUSE_STEPS_PATHS.AboutHousehold;
export const WizardStepAboutHouseHold: TWizardStepComponentEditable<IWizardHouseState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const { userProfile } = useContextHospi();
    const [values, setValues] = useState<
      TStepPayload<IHouseStepAboutHousehold>
    >(() => {
      const ownerName = `${userProfile?.first_name} ${userProfile?.last_name}`;
      if (state[statePath]) {
        if (!state[statePath]?.people_living) {
          return {
            ...state[statePath],
            people_living: [ownerName]
          };
        } else {
          return state[statePath];
        }
      }
      return {
        people_living: [ownerName]
      };
    });
    const [errors, setErrors] = useState<TStepErrors<typeof values>>(null);

    useImperativeHandle(ref, () => ({
      save: () => {
        setErrors(null);
        return HouseAPI.wizardSaveAboutHousehold(
          state.house_id!,
          state.wizard_type!,
          values
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      },
      savePartially: () => {
        setErrors(null);
        return HouseAPI.wizardSaveAboutHouseholdPartially(
          state.house_id!,
          state.wizard_type!,
          getChangedFields(state[statePath], values)
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      }
    }));

    return (
      <WizardStepAboutHouseHoldView
        values={values}
        setValues={setValues}
        errors={errors}
        readonly={readonly}
        viewonly={viewonly}
      />
    );
  });

const CharacteristicsFormItem = styled(Form.Item)``;

const RadioGroup = styled(RadioGroupOriginal)`
  grid-template-columns: 1fr 1fr 1fr;
  column-gap: 24px;
  row-gap: 16px;

  .ant-radio-wrapper {
    padding: 16px;

    @media ${(props) => props.theme.media.tablet},
      ${(props) => props.theme.media.phone} {
      padding: 8px 16px;
      .ant-radio {
        display: none;
      }
    }
  }
`;

const PetsRadioGroup = styled(RadioGroup)`
  grid-template-columns: 1fr;
`;

const PetsButtonSelect = styled(WizardButtonSelect)`
  grid-template-columns: 1fr 1fr 1fr 1fr;
`;

const Root = styled(Form)`
  @media (max-width: 600px) {
    ${PetsButtonSelect} {
      grid-template-columns: 1fr 1fr;
    }

    ${RadioGroup} {
      grid-template-columns: 1fr;
    }
  }
`;

export const WizardStepAboutHouseHoldView: TWizardStepComponentEditableView<IHouseStepAboutHousehold> =
  React.memo(
    ({ errors, values, setValues, viewonly, readonly, enabledFields }) => {
      const { t, i18n } = useTranslation("house");
      const { t: th } = useTranslation("hostrooms");

      const inputSize = "large";

      const characteristicsOptions = HouseCharacteristicsValues.map((v) => ({
        value: v,
        label: t(
          `wizard.steps.AboutHousehold.form.characteristics.options.${v}`
        )
      }));
      const petsOptions = HousePetsValues.map((v) => ({
        value: v,
        label: t(`wizard.steps.AboutHousehold.form.pets.options.${v}`)
      }));

      const currentLanguage: TLang = i18n.language.toUpperCase() as TLang;
      const allLanguagesOptions: ISelectItem[] = [
        {
          value: "ANY",
          label: th("languages.options.ANY")
        },
        ...getLanguage(currentLanguage)
      ];
      const isReadonly = createCheckIsReadonlyStepField(
        readonly,
        enabledFields
      );

      return (
        <Root layout="vertical">
          <CharacteristicsFormItem
            label={t(`wizard.steps.AboutHousehold.form.characteristics.title`)}
            validateStatus={errors?.characteristics ? "error" : ""}
            help={errors?.characteristics ? t("wizard.errors.selection") : ""}
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {
                  characteristicsOptions.find(
                    (v) => values.characteristics === v.value
                  )?.label
                }
              </StepViewOnlyValue>
            ) : (
              <RadioGroup
                value={values.characteristics ?? undefined}
                options={characteristicsOptions}
                disabled={isReadonly("characteristics")}
                onChange={(val) => {
                  setValues({
                    ...values,
                    characteristics: val.target.value
                  });
                }}
              />
            )}
          </CharacteristicsFormItem>
          <Form.Item
            label={t(`wizard.steps.AboutHousehold.form.people_living.title`)}
            validateStatus={errors?.people_living ? "error" : ""}
            help={errors?.people_living}
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {values.people_living?.join(", ") ?? ""}
              </StepViewOnlyValue>
            ) : (
              <EditableListOfItems
                items={values.people_living ?? null}
                onAdd={(item) => {
                  setValues({
                    ...values,
                    people_living: [...(values.people_living ?? []), item]
                  });
                }}
                onRemove={(item) => {
                  setValues({
                    ...values,
                    people_living: values.people_living?.filter(
                      (v) => v !== item
                    )
                  });
                }}
                addNewButtonLabel={t(
                  `wizard.steps.AboutHousehold.form.people_living.addPerson`
                )}
                addNewModalTitle={t(
                  `wizard.steps.AboutHousehold.form.people_living.addPerson`
                )}
                addNewModalFieldLabel={t(
                  `wizard.steps.AboutHousehold.form.people_living.personName`
                )}
                addNewModalFieldError={t(
                  `wizard.steps.AboutHousehold.form.people_living.personUnique`
                )}
                readonly={isReadonly("people_living")}
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(`wizard.steps.AboutHousehold.form.pets.title`)}
            validateStatus={errors?.pets ? "error" : ""}
            help={errors?.pets ? t("wizard.errors.selection") : ""}
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {petsOptions
                  .filter((v) => values.pets?.includes(v.value))
                  .map((o) => o.label)
                  ?.join(", ")}
              </StepViewOnlyValue>
            ) : (
              <PetsButtonSelect
                value={values.pets ?? undefined}
                options={petsOptions.filter((o) => o.value !== "no_pets")}
                disabled={isReadonly("pets")}
                onChange={(val) => {
                  setValues({
                    ...values,
                    pets: (val as THousePets[]).filter((v) => v !== "no_pets")
                  });
                }}
                extraButtons={
                  <PetsRadioGroup
                    value={values.pets?.[0] ?? undefined}
                    options={petsOptions.filter((o) => o.value === "no_pets")}
                    onChange={(val) => {
                      setValues({
                        ...values,
                        pets: [val.target.value]
                      });
                    }}
                  />
                }
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(`wizard.steps.AboutHousehold.form.spoken_languages.title`)}
            validateStatus={errors?.spoken_languages ? "error" : ""}
            help={errors?.spoken_languages ? t("wizard.errors.selection") : ""}
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {allLanguagesOptions
                  .filter((c) =>
                    values.spoken_languages?.includes(c.value as string)
                  )
                  ?.map((o) => o.label)
                  .join(", ") ?? ""}
              </StepViewOnlyValue>
            ) : (
              <Select
                size={inputSize}
                mode="multiple"
                disabled={isReadonly("spoken_languages")}
                value={values.spoken_languages ?? undefined}
                placeholder={t(
                  `wizard.steps.AboutHousehold.form.spoken_languages.placeholder`
                )}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={allLanguagesOptions}
                onChange={(newValue) =>
                  setValues({ ...values, spoken_languages: newValue })
                }
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(`wizard.steps.AboutHousehold.form.description.title`)}
            validateStatus={errors?.description ? "error" : ""}
            help={errors?.description}
          >
            {viewonly ? (
              <StepViewOnlyValue>{values.description ?? ""}</StepViewOnlyValue>
            ) : (
              <Input.TextArea
                size={inputSize}
                disabled={isReadonly("description")}
                value={values.description ?? undefined}
                placeholder={t(
                  `wizard.steps.AboutHousehold.form.description.placeholder`
                )}
                rows={3}
                onChange={(e) => {
                  setValues({
                    ...values,
                    description: e.target.value
                  });
                }}
              />
            )}
          </Form.Item>
        </Root>
      );
    }
  );
